import { Icon, EditableRow, EditableColumn, Switch, ClearDiv, Button, Loading, ThresholdPill } from '@aon/aon-tws-common-ui';
import {
	DeleteIconContainer,
	StyledTable,
	TableBody,
	TableColumn,
	TableHead,
} from '@aon/aon-tws-common-ui/build/lib/components/EditableTable/SharedComponents';
import { TranslationEditorContainer } from '../../../containers/translations/TranslationEditorContainer';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { EditState, ITranslationEditItem } from '../../../models';
import { ActionCreators, InitDefaultTranslationEditItem } from '../../../store/SurveyStore';
import { ISurveyTermItemEditModel, SurveyTermItemEditFieldType } from '../../../models/ISurveyTermItemEditModel';
import { ActionTypes } from '../../../store/ActionTypes';
import { BodyCopyMediumContainer, Col3Col9, LabelAbove, LimitContainer, LoadingWrapper } from '../../common';
import { AddSurveyDefinition } from '../../../store/SurveyDefinitionsSlice';
import { SurveyDefinitionsPreviewModule } from './SurveyDefinitionsPreviewModule';
import { IApplicationState } from '../../../store';
import { ITranslationEditorInstance } from '../../../store/TranslationEditorStore';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import constants from '../../../constants';
import { SurveyDefinitionPageDescription } from './SurveyDefinitionPageDescription';
import { useSurveyDefaultCultureCode } from '../../../utilities/hooks';

const TableBottomBarContainer = styled.div`
	justify-content: space-between;
	width: ${globalVars.fullWidth};
	display: inline-flex;
`;

export const AddButtonContainer = styled.div`
	margin-top: 5px;
	text-align: left;
	display: inline-block;
	span {
		margin-left: 5px;
	}
`;

const ToggleButtonContainer = styled.div`
	display: flex;
	justify-content: space-between;
`;

const EditIconContainer = styled.span`
	font-size: ${globalVars.fontSizeXsmall};
	color: ${globalVars.gray3};
	cursor: pointer;
`;

const GrayedOutEditableColumn = styled(EditableColumn)`
	border: ${globalVars.border1} ${globalVars.borderSolid} ${globalVars.gray3} !important;
`;

const GrayedOutDeleteIconContainer = styled(DeleteIconContainer)`
	color: ${globalVars.gray3} !important;
`;

const BoldTableHead = styled(TableHead)`
	.tws-editable-row .tws-editable-column .tws-editable-textarea {
		font-weight: ${globalVars.fontWeightThick} !important;
	}
`;
interface IEditTranslationsButtonProps {
	expectedCount: number;
	actualCount: number;
	checkThreshold: boolean;
	loading: boolean;
	onClick: () => void;
	disabled: boolean;
}

function EditTranslationsButton(props: IEditTranslationsButtonProps) {
	const { onClick, disabled } = props;

	function handleClick() {
		if (!disabled) onClick();
	}
	return (
		<EditIconContainer onClick={handleClick}>
			<Icon name='edit' />
		</EditIconContainer>
	);
}

export interface ISurveyDefinitionsProps {
	readOnly: boolean;
	saveInModal?: boolean;
}

export interface ISurveyDefinitionRowProps {
	isHeader: boolean;
	canDelete: boolean;
	surveyTermItem: ISurveyTermItemEditModel;
	supportedCultures: string[];
	surveyId: number;
	translations: { [key: string]: ITranslationEditorInstance };
	numberOfSupportedCultures?: number;
	onFocusChanged?: (translationEditItem: ITranslationEditItem, column: string) => void;
	onTextChanged?: (text: string, column: string) => void;
	readOnly: boolean;
	saveInModal?: boolean;
}

export function SurveyDefinitionRow(props: ISurveyDefinitionRowProps) {
	const {
		isHeader,
		canDelete,
		surveyTermItem,
		supportedCultures,
		surveyId,
		translations,
		onFocusChanged,
		onTextChanged,
		readOnly,
		saveInModal,
	} = props;
	const dispatch = useDispatch();
	const defaultCultureCode = useSurveyDefaultCultureCode();

	const termPropertyName = `survey-term-items/${surveyTermItem.surveyTermItemId}/term`;
	const descriptionPropertyName = `survey-term-items/${surveyTermItem.surveyTermItemId}/description`;

	const numberOfTermTranslations = GetNumberOfTranslations(termPropertyName);
	const numberOfDescriptionTranslations = GetNumberOfTranslations(descriptionPropertyName);
	const numberOfSupportedCultures = supportedCultures.filter((c) => c !== defaultCultureCode).length;
	const defaultTranslationEditItem = InitDefaultTranslationEditItem(defaultCultureCode);

	function GetNumberOfTranslations(property: string) {
		if (translations && translations[property]) {
			return translations[property].translations.filter((c) => c.cultureCode !== defaultCultureCode && c.textValue).length;
		} else {
			return 0;
		}
	}

	function GetMissingTranslationsErrorMessage(numberOfTranslations: number) {
		return `Missing translations (${numberOfTranslations} of ${numberOfSupportedCultures} translated) !`;
	}

	function HandleSurveyDefinitionChange(
		surveyTermItemId: number,
		sequence: number,
		editFieldType: SurveyTermItemEditFieldType,
		value: string,
	) {
		dispatch({
			type: ActionTypes.SURVEY_TERM_ITEM_CHANGE,
			payload: {
				surveyTermItemId,
				defaultCultureCode,
				sequence,
				surveyTermItemEditValue: value,
				surveyTermItemEditFieldType: editFieldType,
			},
		});
	}

	function HandleSurveyDefinitionDelete() {
		dispatch({
			type: ActionTypes.SURVEY_TERM_ITEM_DELETE,
			payload: {
				...surveyTermItem,
				deleted: true,
				term: {
					...surveyTermItem.term,
					editState: EditState.Remove,
				},
				description: {
					...surveyTermItem.description,
					editState: EditState.Remove,
				},
			},
		});
	}

	function UpdateSurveyDefinitionTranslation() {
		dispatch({ type: ActionTypes.SURVEY_TERM_TRANSLATION_UPDATED });
	}

	return (
		<EditableRow
			key={`editable-row-${surveyTermItem.surveyTermItemId}-${surveyTermItem.sequence}`}
			record={{ term: surveyTermItem.term?.textValue || '', description: surveyTermItem.description?.textValue || '', isNew: true }}
		>
			<GrayedOutEditableColumn
				key={`editable-column-${surveyTermItem.surveyTermItemId}-${surveyTermItem.sequence}-term`}
				editMode={!readOnly}
				column={{ columnKey: 'term', type: 'textarea' }}
				record={{ term: surveyTermItem.term?.textValue || '', isNew: !readOnly }}
				placeholder={isHeader ? 'Add Column 1 Title' : 'Add information'}
				onChange={(column: string, text: string) => {
					HandleSurveyDefinitionChange(
						surveyTermItem.surveyTermItemId,
						surveyTermItem.sequence,
						SurveyTermItemEditFieldType.SURVEY_DEFINITION_TERM,
						text,
					);
					onTextChanged && onTextChanged(text, column);
				}}
				onFocus={() => onFocusChanged && onFocusChanged(surveyTermItem.term || defaultTranslationEditItem, 'term')}
				hasError={numberOfTermTranslations < numberOfSupportedCultures}
				message={GetMissingTranslationsErrorMessage(numberOfTermTranslations)}
			/>
			<TableColumn>
				<TranslationEditorContainer
					property={termPropertyName}
					inputType={'textarea'}
					maxLength={constants.SURVEY_DEFINITION_TERM_CHARACTER_LIMIT}
					propertyDisplayName={ContentConstants.SUBSECTIONHEADERS_SURVEY_DEFINITION_TERM}
					supportedCultures={supportedCultures}
					uniqueId={surveyTermItem.surveyTermItemId}
					surveyId={surveyId}
					defaultTranslation={surveyTermItem.term ?? defaultTranslationEditItem}
					notifyUpdate={UpdateSurveyDefinitionTranslation}
					modalToggleButton={EditTranslationsButton}
					disabled={readOnly}
					saveInModal={saveInModal}
				/>
			</TableColumn>
			<GrayedOutEditableColumn
				key={`editable-column-${surveyTermItem.surveyTermItemId}-${surveyTermItem.sequence}-description`}
				editMode={!readOnly}
				existingEditable={!readOnly}
				column={{ columnKey: 'description', type: 'textarea' }}
				record={{ description: surveyTermItem.description?.textValue || '', isNew: !readOnly }}
				placeholder={isHeader ? 'Add Column 2 Title' : 'Add information here'}
				onChange={(column: string, text: string) => {
					HandleSurveyDefinitionChange(
						surveyTermItem.surveyTermItemId,
						surveyTermItem.sequence,
						SurveyTermItemEditFieldType.SURVEY_DEFINITION_DESCRIPTION,
						text,
					);
					onTextChanged && onTextChanged(text, column);
				}}
				onFocus={() => onFocusChanged && onFocusChanged(surveyTermItem.description || defaultTranslationEditItem, 'description')}
				hasError={numberOfDescriptionTranslations < numberOfSupportedCultures}
				message={GetMissingTranslationsErrorMessage(numberOfDescriptionTranslations)}
			/>

			<TableColumn>
				<TranslationEditorContainer
					property={descriptionPropertyName}
					inputType={'textarea'}
					maxLength={constants.SURVEY_DEFINITION_DESCRIPTION_CHARACTER_LIMIT}
					propertyDisplayName={ContentConstants.SUBSECTIONHEADERS_SURVEY_DEFINITION_DESC}
					supportedCultures={supportedCultures}
					uniqueId={surveyTermItem.surveyTermItemId}
					surveyId={surveyId}
					defaultTranslation={surveyTermItem.description ?? defaultTranslationEditItem}
					notifyUpdate={UpdateSurveyDefinitionTranslation}
					modalToggleButton={EditTranslationsButton}
					disabled={readOnly}
					saveInModal={saveInModal}
				/>
			</TableColumn>
			{canDelete && (
				<TableColumn>
					<GrayedOutDeleteIconContainer editMode={true} onClick={HandleSurveyDefinitionDelete}>
						<Icon name='delete' />
					</GrayedOutDeleteIconContainer>
				</TableColumn>
			)}
		</EditableRow>
	);
}

SurveyDefinitionRow.defaultProps = {
	saveInModal: true,
};

export interface ISurveyDefinitionsTableProps {
	surveyId: number;
	surveyTermItems: ISurveyTermItemEditModel[];
	supportedCultures: string[];
	readOnly: boolean;
	saveInModal?: boolean;
}

export function SurveyDefinitionsTable(props: ISurveyDefinitionsTableProps) {
	const { surveyTermItems, surveyId, supportedCultures, saveInModal } = props;
	const dispatch = useDispatch();

	const allTranslations = useSelector((state: IApplicationState) => state.translations.instances);

	const [thresholdPillState, setThresholdPillState] = useState({
		threshold: constants.SURVEY_DEFINITION_DESCRIPTION_CHARACTER_LIMIT,
		show: false,
		textCount: 0,
	});

	function getThreshold(column: string): number {
		return column === 'term' ? constants.SURVEY_DEFINITION_TERM_CHARACTER_LIMIT : constants.SURVEY_DEFINITION_DESCRIPTION_CHARACTER_LIMIT;
	}

	function OnFocusChangedHandler(translationEditItem: ITranslationEditItem, column: string) {
		setThresholdPillState({
			threshold: getThreshold(column),
			show: true,
			textCount: translationEditItem.textValue?.length || 0,
		});
	}

	function OnBlurHandler(event: any) {
		setThresholdPillState({
			...thresholdPillState,
			show: false,
		});
	}

	function OnTextChangedHandler(text: string, column: string) {
		setThresholdPillState({
			...thresholdPillState,
			textCount: text?.length || 0,
			threshold: getThreshold(column),
		});
	}

	function AddSurveyDefinitionRow() {
		if (!props.readOnly) {
			// next sequence value = max sequence value + 1
			const nextSequenceValue = Math.max(...surveyTermItems.map((term) => term.sequence)) + 1;
			dispatch(AddSurveyDefinition({ surveyId, sequence: nextSequenceValue, surveyTermItemId: 0 }));
		}
	}

	// Here is the convention that
	// 1) We always treat the survey term which sequence === 1 as the column title
	// 2) Sequences of all survey terms should be unique

	const headRow = surveyTermItems.filter((term) => term.sequence === 1);
	const bodyRows = surveyTermItems.filter((term) => term.sequence > 1 && !term.deleted);

	return (
		<>
			{surveyTermItems && (
				<StyledTable editModel={true} onBlur={OnBlurHandler}>
					<BoldTableHead>
						{headRow.map((term) => (
							<SurveyDefinitionRow
								key={`survey-definition-table-header-${term.surveyTermItemId}-${term.sequence}`}
								surveyTermItem={term}
								isHeader={true}
								canDelete={false}
								supportedCultures={supportedCultures}
								surveyId={surveyId}
								translations={allTranslations}
								onFocusChanged={OnFocusChangedHandler}
								onTextChanged={OnTextChangedHandler}
								readOnly={props.readOnly}
								saveInModal={saveInModal}
							/>
						))}
					</BoldTableHead>
					<TableBody>
						{bodyRows.map((term) => (
							<SurveyDefinitionRow
								key={`survey-definition-table-row-${term.surveyTermItemId}=${term.sequence}`}
								surveyTermItem={term}
								isHeader={false}
								canDelete={bodyRows.length > 1 && !props.readOnly}
								supportedCultures={supportedCultures}
								surveyId={surveyId}
								translations={allTranslations}
								onFocusChanged={OnFocusChangedHandler}
								onTextChanged={OnTextChangedHandler}
								readOnly={props.readOnly}
								saveInModal={saveInModal}
							/>
						))}
					</TableBody>
				</StyledTable>
			)}
			<ClearDiv clearHeight={'10px'} />
			<TableBottomBarContainer>
				<AddButtonContainer>
					<Button id='addSurveyDefinitionButton' floating={true} size='xs' onClick={AddSurveyDefinitionRow}>
						<Icon name='add' />
					</Button>
					<span>{ContentConstants.BUTTONS_ADD_ROW}</span>
				</AddButtonContainer>
				{thresholdPillState.show && (
					<LimitContainer className='tws-character-limit-container'>
						<BodyCopyMediumContainer>{ContentConstants.CUSTOM_QUESTION_RECOMMENDED_TEXT}</BodyCopyMediumContainer>
						<ThresholdPill
							size='sm'
							count={thresholdPillState.textCount}
							threshold={thresholdPillState.threshold}
							showThresholdCount={true}
						/>
					</LimitContainer>
				)}
			</TableBottomBarContainer>
		</>
	);
}

SurveyDefinitionsTable.defaultProps = {
	saveInModal: true,
};

export function SurveyDefinitions(props: ISurveyDefinitionsProps) {
	const dispatch = useDispatch();
	const { surveyEditModel } = useSelector((storeState: IApplicationState) => storeState.survey);
	const { readOnly, saveInModal } = props;
	const { survey, surveyTermItems, surveyTerm } = surveyEditModel;
	const { surveyId } = survey;
	const { isLoading } = surveyTerm;
	const isSurveyTermEnabled = surveyTerm.isActive;

	function ToggleSurveyDefinitions(checked: boolean) {
		if (checked && !(surveyTermItems && surveyTermItems.length > 0)) {
			dispatch(ActionCreators.InitializeSurveyDefinitions(surveyId));
		} else {
			dispatch(ActionCreators.HandleToggleSurveyDefinitionsSwitch(checked));
		}
	}

	return (
		<>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_DEFINITIONS}>
				<LabelAbove type='input'>{ContentConstants.INPUTLABEL_SURVEY_DEFINITIONS}</LabelAbove>
				<ToggleButtonContainer>
					<Switch
						getValue={(checked: any) => ToggleSurveyDefinitions(checked)}
						checked={isSurveyTermEnabled}
						disabled={readOnly || isLoading}
					/>
				</ToggleButtonContainer>
			</Col3Col9>
			{isLoading && (
				<LoadingWrapper>
					<Loading height='70px' width='70px' />
				</LoadingWrapper>
			)}
			{!isLoading && isSurveyTermEnabled && (
				<>
					<SurveyDefinitionPageDescription
						supportedCultures={surveyEditModel.supportedCultures}
						surveyTerm={surveyTerm}
						surveyId={surveyId}
						readOnly={readOnly}
						saveInModal={saveInModal}
					/>
					<Col3Col9>
						<SurveyDefinitionsPreviewModule
							supportedCultures={surveyEditModel.supportedCultures}
							defaultCultureCode={surveyEditModel.defaultCultureCode}
							surveyTerm={surveyTerm}
							surveyTermItems={surveyTermItems}
							surveyId={surveyId}
							disabled={readOnly}
						/>
						<ClearDiv clearHeight={'15px'} />
						<SurveyDefinitionsTable
							surveyTermItems={surveyTermItems}
							surveyId={surveyId}
							supportedCultures={surveyEditModel.supportedCultures}
							readOnly={readOnly}
							saveInModal={saveInModal}
						/>
					</Col3Col9>
				</>
			)}
		</>
	);
}

SurveyDefinitions.defaultProps = {
	saveInModal: true,
};
